import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Loader2, UserPlus, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { LOGO } from "@/lib/brand";

export default function AcceptInvite() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const [invite, setInvite] = useState(null);
  const [err, setErr] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get(`/public/invite/${token}`).then((r) => setInvite(r.data)).catch((e) => setErr(apiErr(e)));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post(`/public/invite/${token}/accept`, form);
      localStorage.setItem("kp_token", data.token);
      await refreshUser();
      toast.success("Welcome to the team!");
      navigate("/dashboard");
    } catch (e2) { toast.error(apiErr(e2)); }
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-6" data-testid="accept-invite-page">
      <div className="w-full max-w-sm">
        <img src={LOGO} alt="manage.kitchen" className="h-14 w-auto object-contain mb-8" />
        {err ? (
          <div className="text-center bg-card border border-border rounded-xl p-8" data-testid="invite-error">
            <AlertCircle className="h-10 w-10 text-muted-foreground mx-auto" />
            <p className="mt-4 text-muted-foreground">{err}</p>
            <Link to="/login" className="text-primary font-medium hover:underline mt-4 inline-block">Go to login</Link>
          </div>
        ) : !invite ? (
          <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
        ) : (
          <>
            <div className="flex items-center gap-2 text-primary mb-2"><UserPlus className="h-5 w-5" /><span className="text-sm font-medium uppercase tracking-wide">Staff invitation</span></div>
            <h1 className="font-heading text-3xl font-medium">Join {invite.restaurant_name}</h1>
            <p className="text-muted-foreground mt-1 text-sm">You've been invited as <span className="font-medium text-foreground">{invite.staff_role}</span>. Set up your login below.</p>
            <form onSubmit={submit} className="mt-8 space-y-4">
              <div className="space-y-1.5"><Label>Your name</Label>
                <Input data-testid="invite-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Alex Kim" /></div>
              <div className="space-y-1.5"><Label>Email</Label>
                <Input data-testid="invite-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="you@email.com" /></div>
              <div className="space-y-1.5"><Label>Choose a password</Label>
                <Input data-testid="invite-password" type="password" required minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="At least 6 characters" /></div>
              <Button data-testid="invite-submit" type="submit" disabled={loading} className="w-full bg-primary hover:bg-[#B35A42] text-primary-foreground">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create my account"}
              </Button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
